/**
 * Onboarding walkthrough types.
 *
 * Mirrors backend `user/onboarding.py`:
 *   - OnboardingProgressSerializer
 *
 * Step keys (must match ONBOARDING_STEPS in backend):
 *   "welcome" - initial modal
 *   "people" - user navigated to /people
 *   "interaction" - user logged a first Interaction
 *   "copilot" - user opened the copilot drawer
 *   "playbook" - user launched any playbook
 */
export type OnboardingStepKey =
  | "welcome"
  | "people"
  | "interaction"
  | "copilot"
  | "playbook";

export interface OnboardingProgress {
  completed_steps: string[];
  /** Step keys the user soft-skipped ("Skip for now"). The walkthrough
   *  continues; they can come back to these. */
  deferred_steps: string[];
  current_step: string;
  dismissed_at: string | null;
  completed_at: string | null;
  is_dismissed: boolean;
  is_complete: boolean;
  all_steps: OnboardingStepKey[];
  created_at: string;
  updated_at: string;
}

export interface OnboardingStepDefinition {
  key: OnboardingStepKey;
  title: string;
  body: string;
  /** CTA label on the step card. */
  cta: string;
  /** Route to send the user to when they click CTA. */
  cta_route?: string;
  /** Optional preamble shown after step completion. */
  done_message?: string;
}

/**
 * The on-screen content for each step. Lives in the FE because copy
 * iteration shouldn't require a BE migration.
 */
export const STEP_CONTENT: Record<OnboardingStepKey, OnboardingStepDefinition> = {
  welcome: {
    key: "welcome",
    title: "Welcome to TNN Operations",
    body: "This pilot is built to handle the operational drag your team feels every day - the donor receipts that go out late, the volunteer shifts in three different spreadsheets, the 45-minute family intakes. We'll walk you through five things in the next two minutes. You can skip anytime.",
    cta: "Show me around",
  },
  people: {
    key: "people",
    title: "Add your first person record",
    body: "Community holds donors and support recipients. Team holds volunteers, board, and staff. Roles still stack on a profile when someone wears more than one hat.",
    cta: "Open Community",
    cta_route: "/community",
    done_message: "Browse the list, click a profile when you're ready.",
  },
  interaction: {
    key: "interaction",
    title: "Log a donation or interaction",
    body: "Every donation, call, meeting, shift, or service you provide gets recorded as an Interaction. These are the events your KPIs are built from. Open any person's profile and add one - note, call, donation, whatever fits.",
    cta: "Find someone to log",
    cta_route: "/community",
    done_message: "Nice - that's how the system learns what your team does.",
  },
  copilot: {
    key: "copilot",
    title: "Try the AI Copilot",
    body: "The chat bubble in the bottom-right is your AI copilot. It can search your people, summarize donations, help you draft thank-yous, and run multi-step playbooks. Click it to say hello.",
    cta: "Open the copilot",
    done_message: "Ask it anything - 'how are our KPIs?' is a good start.",
  },
  playbook: {
    key: "playbook",
    title: "Launch your first playbook",
    body: "Playbooks are the multi-step recipes your team will use most: ESL family intake, donor thank-yous, volunteer signup, grant-application help, program-participant intake. The copilot runs them autonomously. Try one.",
    cta: "Launch a playbook",
    done_message: "That's the loop - playbook in, work done. You're set.",
  },
};
